import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useUsersStore, User } from '../store/users';
import { api } from '../services/api';
import { PageHeader } from '../components/PageHeader';

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  editor: 'bg-blue-100 text-blue-800',
  viewer: 'bg-gray-100 text-gray-600',
};

function CreateUserModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { email: string; password: string; name?: string; role?: string }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onCreate({ email, password, name: name || undefined, role });
      setEmail('');
      setPassword('');
      setName('');
      setRole('editor');
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">{t('admin:userManagement.newUser')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.password')}</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.role')}</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'editor' | 'viewer')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="admin">{t('admin:userManagement.roles.admin')}</option>
              <option value="editor">{t('admin:userManagement.roles.editor')}</option>
              <option value="viewer">{t('admin:userManagement.roles.viewer')}</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('admin:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('admin:userManagement.creating') : t('admin:userManagement.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({
  isOpen,
  onClose,
  user,
  onSave,
  onRefresh,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSave: (id: string, data: Partial<User>) => Promise<void>;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    role: 'editor' as 'admin' | 'editor' | 'viewer',
    phone: '',
    company: '',
    position: '',
    bio: '',
    // Adresse
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    // URLs
    website: '',
    linkedin_url: '',
    xing_url: '',
  });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Passwort-Änderung
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        role: user.role,
        phone: user.phone || '',
        company: user.company || '',
        position: user.position || '',
        bio: user.bio || '',
        street: user.street || '',
        house_number: user.house_number || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
        country: user.country || 'Deutschland',
        website: user.website || '',
        linkedin_url: user.linkedin_url || '',
        xing_url: user.xing_url || '',
      });
      setAvatarUrl(user.avatar_url);
      setMessage(null);
      // Passwortfelder zurücksetzen
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 150 * 1024) {
      setMessage({ type: 'error', text: t('admin:avatarTooLarge') });
      return;
    }

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setMessage({ type: 'error', text: t('admin:avatarWrongFormat') });
      return;
    }

    const uploadData = new FormData();
    uploadData.append('avatar', file);

    setIsUploadingAvatar(true);
    setMessage(null);
    try {
      const response = await fetch(`/api/users/${user.id}/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${api.getAccessToken()}`,
        },
        credentials: 'include',
        body: uploadData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('admin:uploadError'));
      }

      const data = await response.json();
      setAvatarUrl(data.data.avatar_url);
      setMessage({ type: 'success', text: t('admin:avatarUpdated') });
      onRefresh();
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : t('admin:errors.uploadFailed') });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);
    try {
      await onSave(user.id, formData);
      setMessage({ type: 'success', text: t('admin:userSaved') });
      setTimeout(() => onClose(), 1000);
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : t('admin:errorSaving') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      setMessage({ type: 'error', text: t('admin:passwordEnterNew') });
      return;
    }
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: t('admin:passwordTooShort') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('admin:passwordNoMatch') });
      return;
    }

    setIsChangingPassword(true);
    setMessage(null);
    try {
      await api.post(`/users/${user.id}/change-password`, { newPassword });
      setMessage({ type: 'success', text: t('admin:passwordChanged') });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : t('admin:passwordChangeError') });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = () => {
    if (formData.name) {
      return formData.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user.email[0].toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header with Avatar */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-6 rounded-t-lg">
          <div className="flex items-center">
            <div className="relative group">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="relative focus:outline-none"
                disabled={isUploadingAvatar}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={formData.name || user.email}
                    className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-lg">
                    <span className="text-blue-600 text-2xl font-bold">{getInitials()}</span>
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">
                    {isUploadingAvatar ? '...' : t('settings:change')}
                  </span>
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
            <div className="ml-4 text-white">
              <h2 className="text-xl font-semibold">{t('admin:userManagement.editUser')}</h2>
              <p className="text-blue-100">{user.email}</p>
              <p className="text-blue-200 text-xs font-mono">{user.display_id}</p>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`px-6 py-3 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Rolle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.role')}</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'editor' | 'viewer' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="admin">{t('admin:userManagement.roles.admin')}</option>
                <option value="editor">{t('admin:userManagement.roles.editor')}</option>
                <option value="viewer">{t('admin:userManagement.roles.viewer')}</option>
              </select>
            </div>

            {/* Telefon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.phone')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+49 123 456789"
              />
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.position')}</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Unternehmen */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.company')}</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Bio */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.bio')}</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Adresse */}
          <h4 className="text-sm font-medium text-gray-800 mt-6 mb-3 pt-4 border-t border-gray-200">{t('settings:address.title')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:address.street')}</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:address.houseNumber')}</label>
              <input
                type="text"
                value={formData.house_number}
                onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:address.postalCode')}</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:address.city')}</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:address.country')}</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* URLs */}
          <h4 className="text-sm font-medium text-gray-800 mt-6 mb-3 pt-4 border-t border-gray-200">{t('settings:onlineProfiles.title')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:onlineProfiles.website')}</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:onlineProfiles.linkedin')}</label>
              <input
                type="url"
                value={formData.linkedin_url}
                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings:onlineProfiles.xing')}</label>
              <input
                type="url"
                value={formData.xing_url}
                onChange={(e) => setFormData({ ...formData, xing_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://xing.com/profile/..."
              />
            </div>
          </div>

          {/* Passwort ändern */}
          <h4 className="text-sm font-medium text-gray-800 mt-6 mb-3 pt-4 border-t border-gray-200">{t('admin:userManagement.changePassword')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.newPassword')}</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('admin:userManagement.passwordMinLength')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:userManagement.confirmPassword')}</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('admin:userManagement.repeatPassword')}
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={isChangingPassword || !newPassword || !confirmPassword}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isChangingPassword ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('admin:userManagement.passwordChanging')}
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    {t('admin:userManagement.setPassword')}
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 mt-1">
                {t('admin:userManagement.passwordChangeNote')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('admin:cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('admin:saving') : t('admin:save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserRow({
  user,
  currentUserId,
  onDeactivate,
  onReactivate,
  onEdit,
}: {
  user: User;
  currentUserId: string;
  onDeactivate: (id: string) => void;
  onReactivate: (id: string) => void;
  onEdit: (user: User) => void;
}) {
  const { t } = useTranslation();
  const isCurrentUser = user.id === currentUserId;

  return (
    <tr className={!user.is_active ? 'bg-gray-50 opacity-60' : ''}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name || user.email}
              className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-600 font-medium">
                {(user.name || user.email)[0].toUpperCase()}
              </span>
            </div>
          )}
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {user.name || '-'}
              {isCurrentUser && <span className="ml-2 text-xs text-gray-500">({t('admin:userManagement.you')})</span>}
            </div>
            <div className="text-sm text-gray-500">{user.email}</div>
            <div className="text-xs text-gray-400 font-mono">{user.display_id}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 text-xs rounded-full ${ROLE_COLORS[user.role]}`}>
          {t(`admin:userManagement.roles.${user.role}`)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {user.is_active ? t('admin:userManagement.statuses.active') : t('admin:userManagement.statuses.inactive')}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(user.created_at).toLocaleDateString('de-DE')}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={() => onEdit(user)}
          className="text-blue-600 hover:text-blue-900 mr-3"
        >
          {t('admin:edit')}
        </button>
        {!isCurrentUser && (
          user.is_active ? (
            <button
              onClick={() => onDeactivate(user.id)}
              className="text-red-600 hover:text-red-900"
            >
              {t('admin:userManagement.deactivate')}
            </button>
          ) : (
            <button
              onClick={() => onReactivate(user.id)}
              className="text-green-600 hover:text-green-900"
            >
              {t('admin:userManagement.activate')}
            </button>
          )
        )}
      </td>
    </tr>
  );
}

// Stock API Stats Interface
interface StockApiStats {
  unsplash: {
    configured: boolean;
    requestsThisSession: number;
    rateLimit: number | null;
    remaining: number | null;
    lastReset: string;
    status: 'ok' | 'warning' | 'critical' | 'unconfigured';
  };
  pexels: {
    configured: boolean;
    requestsThisSession: number;
    rateLimit: number | null;
    remaining: number | null;
    lastReset: string;
    status: 'ok' | 'warning' | 'critical' | 'unconfigured';
  };
  downloads: {
    total: number;
    bySource: { unsplash: number; pexels: number };
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
  limits: {
    unsplash: { description: string; demo: string; production: string; currentMode: string };
    pexels: { description: string; limit: string; note: string };
  };
}

export function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { users, stats, fetchUsers, fetchStats, createUser, updateUser, deactivateUser, reactivateUser, isLoading, error } =
    useUsersStore();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [stockStats, setStockStats] = useState<StockApiStats | null>(null);
  const [stockStatsLoading, setStockStatsLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    // Fetch stock API stats
    fetchStockStats();
  }, [fetchUsers, fetchStats]);

  const fetchStockStats = async () => {
    setStockStatsLoading(true);
    try {
      const stats = await api.get<StockApiStats>('/stock/stats');
      setStockStats(stats);
    } catch (err) {
      console.error('Failed to fetch stock stats:', err);
    } finally {
      setStockStatsLoading(false);
    }
  };

  // Redirect non-admins
  if (user && user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <h1 className="text-xl font-semibold text-red-600 mb-4">{t('admin:accessDenied')}</h1>
          <p className="text-gray-600 mb-4">{t('admin:noPermission')}</p>
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            {t('admin:backToDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title={t('admin:title')}
        subtitle={t('admin:subtitle')}
        breadcrumbs={[{ label: 'Dashboard', href: '/' }]}
        actions={
          <div className="flex gap-2">
            <Link
              to="/admin"
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium"
            >
              {t('admin:users')}
            </Link>
            <Link
              to="/admin/tenants"
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
            >
              {t('admin:tenants')}
            </Link>
          </div>
        }
      />

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">{t('admin:stats.total')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <div className="text-sm text-gray-500">{t('admin:stats.active')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-purple-600">{stats.admins}</div>
              <div className="text-sm text-gray-500">{t('admin:stats.admins')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.editors}</div>
              <div className="text-sm text-gray-500">{t('admin:stats.editors')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-600">{stats.viewers}</div>
              <div className="text-sm text-gray-500">{t('admin:stats.viewers')}</div>
            </div>
          </div>
        )}

        {/* Stock API Stats */}
        {stockStats && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('admin:stockApi.title')}</h3>
              <button
                onClick={fetchStockStats}
                disabled={stockStatsLoading}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <svg className={`w-4 h-4 ${stockStatsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('admin:stockApi.refresh')}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Unsplash */}
              <div className={`p-4 rounded-lg border-2 ${
                stockStats.unsplash.status === 'critical' ? 'border-red-300 bg-red-50' :
                stockStats.unsplash.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                stockStats.unsplash.status === 'unconfigured' ? 'border-gray-200 bg-gray-50' :
                'border-green-300 bg-green-50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-black rounded flex items-center justify-center">
                      <span className="text-white text-xs font-bold">U</span>
                    </div>
                    <span className="font-medium">Unsplash</span>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    stockStats.unsplash.status === 'critical' ? 'bg-red-100 text-red-700' :
                    stockStats.unsplash.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                    stockStats.unsplash.status === 'unconfigured' ? 'bg-gray-100 text-gray-600' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {stockStats.unsplash.status === 'unconfigured' ? t('admin:stockApi.unconfigured') :
                     stockStats.unsplash.status === 'critical' ? t('admin:stockApi.critical') :
                     stockStats.unsplash.status === 'warning' ? t('admin:stockApi.warning') : t('admin:stockApi.ok')}
                  </span>
                </div>
                {stockStats.unsplash.configured ? (
                  <>
                    <div className="text-sm text-gray-600 mb-2">
                      {t('admin:stockApi.mode')}: <span className="font-medium">{stockStats.limits.unsplash.currentMode === 'production' ? t('admin:stockApi.production') : t('admin:stockApi.demo')}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('admin:stockApi.remaining')}: <span className="font-semibold">{stockStats.unsplash.remaining ?? '?'}</span> / {stockStats.unsplash.rateLimit ?? '?'} {t('admin:stockApi.requestsPerHour')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('admin:stockApi.session')}: {stockStats.unsplash.requestsThisSession} {t('admin:stockApi.requests')}
                    </div>
                    {stockStats.unsplash.remaining && stockStats.unsplash.rateLimit && (
                      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            (stockStats.unsplash.remaining / stockStats.unsplash.rateLimit) > 0.25 ? 'bg-green-500' :
                            (stockStats.unsplash.remaining / stockStats.unsplash.rateLimit) > 0.1 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${(stockStats.unsplash.remaining / stockStats.unsplash.rateLimit) * 100}%` }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500">
                    UNSPLASH_ACCESS_KEY {t('admin:stockApi.notConfigured')}
                  </div>
                )}
              </div>

              {/* Pexels */}
              <div className={`p-4 rounded-lg border-2 ${
                stockStats.pexels.status === 'critical' ? 'border-red-300 bg-red-50' :
                stockStats.pexels.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                stockStats.pexels.status === 'unconfigured' ? 'border-gray-200 bg-gray-50' :
                'border-green-300 bg-green-50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-teal-500 rounded flex items-center justify-center">
                      <span className="text-white text-xs font-bold">P</span>
                    </div>
                    <span className="font-medium">Pexels</span>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    stockStats.pexels.status === 'critical' ? 'bg-red-100 text-red-700' :
                    stockStats.pexels.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                    stockStats.pexels.status === 'unconfigured' ? 'bg-gray-100 text-gray-600' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {stockStats.pexels.status === 'unconfigured' ? t('admin:stockApi.unconfigured') :
                     stockStats.pexels.status === 'critical' ? t('admin:stockApi.critical') :
                     stockStats.pexels.status === 'warning' ? t('admin:stockApi.warning') : t('admin:stockApi.ok')}
                  </span>
                </div>
                {stockStats.pexels.configured ? (
                  <>
                    <div className="text-sm text-gray-600 mb-2">
                      {t('admin:stockApi.limit')}: <span className="font-medium">{stockStats.limits.pexels.limit}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('admin:stockApi.remaining')}: <span className="font-semibold">{stockStats.pexels.remaining ?? '?'}</span> / {stockStats.pexels.rateLimit ?? '200'} {t('admin:stockApi.requestsPerHour')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('admin:stockApi.session')}: {stockStats.pexels.requestsThisSession} {t('admin:stockApi.requests')}
                    </div>
                    {stockStats.pexels.remaining !== null && (
                      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            (stockStats.pexels.remaining / (stockStats.pexels.rateLimit || 200)) > 0.25 ? 'bg-green-500' :
                            (stockStats.pexels.remaining / (stockStats.pexels.rateLimit || 200)) > 0.1 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${(stockStats.pexels.remaining / (stockStats.pexels.rateLimit || 200)) * 100}%` }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500">
                    PEXELS_API_KEY {t('admin:stockApi.notConfigured')}
                  </div>
                )}
              </div>
            </div>

            {/* Download Stats */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">{t('admin:stockApi.downloadStats')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{stockStats.downloads.total}</div>
                  <div className="text-xs text-gray-500">{t('admin:stats.total')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{stockStats.downloads.today}</div>
                  <div className="text-xs text-gray-500">{t('admin:stockApi.today')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stockStats.downloads.thisWeek}</div>
                  <div className="text-xs text-gray-500">{t('admin:stockApi.thisWeek')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-black">{stockStats.downloads.bySource.unsplash}</div>
                  <div className="text-xs text-gray-500">Unsplash</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-600">{stockStats.downloads.bySource.pexels}</div>
                  <div className="text-xs text-gray-500">Pexels</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin:users')}</h2>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('admin:userManagement.newUser')}
            </button>
          </div>

          {error && (
            <div className="px-6 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          {isLoading ? (
            <div className="px-6 py-12 text-center text-gray-500">{t('admin:loading')}</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin:users')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin:userManagement.role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin:userManagement.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin:userManagement.createdAt')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin:userManagement.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    currentUserId={user?.id || ''}
                    onDeactivate={deactivateUser}
                    onReactivate={reactivateUser}
                    onEdit={setEditingUser}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={createUser}
      />

      <EditUserModal
        isOpen={editingUser !== null}
        onClose={() => setEditingUser(null)}
        user={editingUser}
        onSave={updateUser}
        onRefresh={fetchUsers}
      />
    </div>
  );
}
